// @ts-nocheck — vendored ManualAnnotation camera-math; typechecked upstream, exempt from host strict flags (noUncheckedIndexedAccess/exactOptionalPropertyTypes).
// KB4 (Kannala-Brandt) fisheye camera math + epipolar curve sampling.
//
// Projection model:
//   r   = sqrt((X/Z)² + (Y/Z)²)
//   θ   = atan(r)
//   θ_d = θ·(1 + k1·θ² + k2·θ⁴ + k3·θ⁶ + k4·θ⁸)
//   u   = fx·(θ_d/r)·(X/Z) + cx
//   v   = fy·(θ_d/r)·(Y/Z) + cy
//
// Unprojection inverts the radial polynomial via Newton-Raphson.

type Mat3 = number[][];
type Vec3 = [number, number, number];
type KB4 = [number, number, number, number];

function mat3MulVec3(M: Mat3, v: Vec3): Vec3 {
  return [
    M[0][0] * v[0] + M[0][1] * v[1] + M[0][2] * v[2],
    M[1][0] * v[0] + M[1][1] * v[1] + M[1][2] * v[2],
    M[2][0] * v[0] + M[2][1] * v[1] + M[2][2] * v[2],
  ];
}

function mat3Transpose(M: Mat3): Mat3 {
  return [
    [M[0][0], M[1][0], M[2][0]],
    [M[0][1], M[1][1], M[2][1]],
    [M[0][2], M[1][2], M[2][2]],
  ];
}

// Project a 3D point to fisheye pixel coordinates. Returns null when Z ≤ 0.
export function projectKB4(P: Vec3, K: Mat3, D: KB4): [number, number] | null {
  const [X, Y, Z] = P;
  if (Z <= 0) return null;
  const mx = X / Z;
  const my = Y / Z;
  const r = Math.sqrt(mx * mx + my * my);
  const theta = Math.atan(r);
  const t2 = theta * theta;
  const t4 = t2 * t2;
  const thetaD = theta * (1 + D[0] * t2 + D[1] * t4 + D[2] * t4 * t2 + D[3] * t4 * t4);
  const scale = r > 1e-8 ? thetaD / r : 1.0;
  return [K[0][0] * scale * mx + K[0][2], K[1][1] * scale * my + K[1][2]];
}

// Unproject a fisheye pixel to a 3D ray (Z=1). Inverts radial distortion via
// Newton-Raphson — converges in < 10 iterations for typical fisheye params.
export function unprojectKB4(u: number, v: number, K: Mat3, D: KB4): Vec3 {
  const mxD = (u - K[0][2]) / K[0][0];
  const myD = (v - K[1][2]) / K[1][1];
  const rD = Math.sqrt(mxD * mxD + myD * myD);
  if (rD < 1e-8) return [0, 0, 1];

  // Solve: f(θ) = θ·(1 + k1·θ² + k2·θ⁴ + k3·θ⁶ + k4·θ⁸) − rD = 0
  let theta = rD;
  for (let i = 0; i < 20; i++) {
    const t2 = theta * theta;
    const t4 = t2 * t2;
    const f = theta * (1 + D[0] * t2 + D[1] * t4 + D[2] * t4 * t2 + D[3] * t4 * t4) - rD;
    const df = 1 + 3 * D[0] * t2 + 5 * D[1] * t4 + 7 * D[2] * t4 * t2 + 9 * D[3] * t4 * t4;
    const step = f / df;
    theta -= step;
    if (Math.abs(step) < 1e-9) break;
  }

  const rU = Math.tan(theta);
  const scale = rU / rD;
  return [mxD * scale, myD * scale, 1];
}

// Compute the epipolar curve on the destination camera for a source pixel.
// Samples 80 depth-stratified 3D points along the back-projected ray,
// transforms them to the destination frame, and projects to pixels.
// dstSize is [W, H]; out-of-bounds and behind-camera samples are dropped.
export function computeEpipolarCurve(
  srcPx: [number, number],
  srcK: Mat3,
  srcD: KB4,
  dstK: Mat3,
  dstD: KB4,
  R: Mat3,
  T: Vec3,
  dstSize: [number, number],
  nPoints = 80
): [number, number][] {
  const ray = unprojectKB4(srcPx[0], srcPx[1], srcK, srcD);
  const [W, H] = dstSize;
  const result: [number, number][] = [];

  for (let i = 0; i < nPoints; i++) {
    // Logarithmically spaced depths 0.2 m – 12 m (matches PoseStudio geomspace)
    const depth = 0.2 * 60 ** (i / (nPoints - 1));
    const P_src: Vec3 = [ray[0] * depth, ray[1] * depth, depth];
    const rot = mat3MulVec3(R, P_src);
    const P_dst: Vec3 = [rot[0] + T[0], rot[1] + T[1], rot[2] + T[2]];

    const px = projectKB4(P_dst, dstK, dstD);
    if (!px) continue;
    const [pu, pv] = px;
    if (pu < 0 || pu >= W || pv < 0 || pv >= H) continue;
    result.push([pu, pv]);
  }

  return result;
}

// Return the inverse stereo transform: given R (src→dst) and T (src→dst),
// compute R_inv and T_inv for the reverse direction (dst→src).
export function invertStereo(R: Mat3, T: Vec3): { R_inv: Mat3; T_inv: Vec3 } {
  const R_inv = mat3Transpose(R);
  const Rt = mat3MulVec3(R_inv, T);
  return { R_inv, T_inv: [-Rt[0], -Rt[1], -Rt[2]] };
}

// Split a curve into contiguous segments, breaking where consecutive points
// are more than maxGap image-pixels apart (prevents wrap-around artifacts
// when part of the curve exits and re-enters the image).
export function splitCurveOnGaps(curve: [number, number][], maxGap = 100): [number, number][][] {
  if (curve.length < 2) return curve.length === 1 ? [[curve[0]]] : [];
  const segments: [number, number][][] = [];
  let seg: [number, number][] = [curve[0]];
  for (let i = 1; i < curve.length; i++) {
    const dx = curve[i][0] - curve[i - 1][0];
    const dy = curve[i][1] - curve[i - 1][1];
    if (dx * dx + dy * dy > maxGap * maxGap) {
      if (seg.length > 1) segments.push(seg);
      seg = [curve[i]];
    } else {
      seg.push(curve[i]);
    }
  }
  if (seg.length > 1) segments.push(seg);
  return segments;
}

// ── Scaramuzza (Taylor / OCamCalib) camera model ─────────────────────────────
//
// Unproject: given pixel (u, v) with distortion center (cx, cy) and Taylor
// coefficients [a0, a1, a2, ...], the 3D ray direction is [mx, my, f(rho)]
// where mx = u−cx, my = v−cy, rho = √(mx²+my²), f(ρ) = Σ aᵢ·ρⁱ.
//
// Project: find rho_img such that rho_img/f(rho_img) = √(X²+Y²)/Z via
// Newton-Raphson on h(ρ)=ρ−f(ρ)·slope=0. rhoMax (where f=0) is pre-computed
// once per curve to bound the search.

function evalTaylor(rho: number, taylor: number[]): number {
  let z = 0;
  let pow = 1;
  for (const a of taylor) {
    z += a * pow;
    pow *= rho;
  }
  return z;
}

function evalTaylorDeriv(rho: number, taylor: number[]): number {
  let dz = 0;
  let pow = 1;
  for (let i = 1; i < taylor.length; i++) {
    dz += i * taylor[i] * pow;
    pow *= rho;
  }
  return dz;
}

// Find rho_max: the smallest positive rho where f(rho) = 0 (valid-domain boundary).
function findRhoMax(taylor: number[]): number {
  // Search up to 3000 px (larger than any sensor half-diagonal).
  let lo = 0;
  let hi = 3000;
  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    if (evalTaylor(mid, taylor) > 0) lo = mid;
    else hi = mid;
    if (hi - lo < 0.5) break;
  }
  return lo;
}

export function unprojectScaramuzza(
  u: number,
  v: number,
  cx: number,
  cy: number,
  taylor: number[]
): Vec3 {
  const mx = u - cx;
  const my = v - cy;
  const rho = Math.sqrt(mx * mx + my * my);
  const z = evalTaylor(rho, taylor);
  const norm = Math.sqrt(mx * mx + my * my + z * z);
  if (norm < 1e-10) return [0, 0, 1];
  return [mx / norm, my / norm, z / norm];
}

export function projectScaramuzza(
  P: Vec3,
  cx: number,
  cy: number,
  taylor: number[],
  rhoMax: number
): [number, number] | null {
  const [X, Y, Z] = P;
  if (Z <= 0) return null;
  const rho3D = Math.sqrt(X * X + Y * Y);
  if (rho3D < 1e-10) return [cx, cy];
  const phi = Math.atan2(Y, X);
  const slope = rho3D / Z;

  // Solve h(rho) = rho − f(rho)·slope = 0 via Newton-Raphson.
  // Start from the midpoint of the valid domain.
  let rho = rhoMax / 2;
  for (let iter = 0; iter < 25; iter++) {
    const f = evalTaylor(rho, taylor);
    const df = evalTaylorDeriv(rho, taylor);
    const h = rho - f * slope;
    const hd = 1 - df * slope;
    if (Math.abs(hd) < 1e-12) break;
    const step = h / hd;
    rho -= step;
    if (rho <= 0) rho = 0.5;
    if (rho >= rhoMax) rho = rhoMax - 0.5;
    if (Math.abs(step) < 0.02) break;
  }

  if (rho <= 0 || rho >= rhoMax) return null;
  return [rho * Math.cos(phi) + cx, rho * Math.sin(phi) + cy];
}

export function computeEpipolarCurveScaramuzza(
  srcPx: [number, number],
  srcCx: number,
  srcCy: number,
  srcTaylor: number[],
  dstCx: number,
  dstCy: number,
  dstTaylor: number[],
  R: Mat3,
  T: Vec3,
  dstSize: [number, number],
  nPoints = 80
): [number, number][] {
  const ray = unprojectScaramuzza(srcPx[0], srcPx[1], srcCx, srcCy, srcTaylor);
  const [W, H] = dstSize;
  const rhoMax = findRhoMax(dstTaylor);
  const result: [number, number][] = [];

  for (let i = 0; i < nPoints; i++) {
    const depth = 0.2 * 60 ** (i / (nPoints - 1));
    const P_src: Vec3 = [ray[0] * depth, ray[1] * depth, ray[2] * depth];
    const rot = mat3MulVec3(R, P_src);
    const P_dst: Vec3 = [rot[0] + T[0], rot[1] + T[1], rot[2] + T[2]];

    const px = projectScaramuzza(P_dst, dstCx, dstCy, dstTaylor, rhoMax);
    if (!px) continue;
    const [pu, pv] = px;
    if (pu < 0 || pu >= W || pv < 0 || pv >= H) continue;
    result.push([pu, pv]);
  }

  return result;
}

// ── Double Sphere camera model ───────────────────────────────────────────────
//
// Reference: Usenko, Demmel, Cremers, "The Double Sphere Camera Model",
// 3DV 2018. Params = [fx, fy, cx, cy, xi, alpha].
//
// Project (3D point P=[X,Y,Z] → pixel):
//   d1 = √(X² + Y² + Z²)
//   d2 = √(X² + Y² + (xi·d1 + Z)²)
//   denom = alpha·d2 + (1-alpha)·(xi·d1 + Z)
//   u = fx·X/denom + cx, v = fy·Y/denom + cy
//
// Unproject (pixel → 3D ray):
//   mx = (u-cx)/fx, my = (v-cy)/fy, r² = mx²+my²
//   mz = (1 - alpha²·r²) / (alpha·√(1-(2·alpha-1)·r²) + (1-alpha))
//   k  = (mz·xi + √(mz² + (1-xi²)·r²)) / (mz² + r²)
//   ray = [k·mx, k·my, k·mz - xi]

type DS = [number, number, number, number, number, number]; // [fx, fy, cx, cy, xi, alpha]

export function projectDs(P: Vec3, ds: DS): [number, number] | null {
  const [X, Y, Z] = P;
  const [fx, fy, cx, cy, xi, alpha] = ds;
  const d1 = Math.sqrt(X * X + Y * Y + Z * Z);
  const t = xi * d1 + Z;
  const d2 = Math.sqrt(X * X + Y * Y + t * t);
  const denom = alpha * d2 + (1 - alpha) * t;
  // Validity: forward-facing rays only. Mirrors the paper's z > -w2·d1 check.
  if (denom < 1e-8) return null;
  const w1 = alpha <= 0.5 ? alpha / (1 - alpha) : (1 - alpha) / alpha;
  const w2 = (w1 + xi) / Math.sqrt(2 * w1 * xi + xi * xi + 1);
  if (Z <= -w2 * d1) return null;
  return [(fx * X) / denom + cx, (fy * Y) / denom + cy];
}

export function unprojectDs(u: number, v: number, ds: DS): Vec3 | null {
  const [fx, fy, cx, cy, xi, alpha] = ds;
  const mx = (u - cx) / fx;
  const my = (v - cy) / fy;
  const r2 = mx * mx + my * my;
  // Validity in image plane (only meaningful when alpha > 0.5).
  if (alpha > 0.5 && r2 >= 1 / (2 * alpha - 1)) return null;
  const inner = 1 - (2 * alpha - 1) * r2;
  if (inner < 0) return null;
  const mz = (1 - alpha * alpha * r2) / (alpha * Math.sqrt(inner) + (1 - alpha));
  const k2 = mz * mz + (1 - xi * xi) * r2;
  if (k2 < 0) return null;
  const denom = mz * mz + r2;
  if (denom < 1e-12) return [0, 0, 1];
  const k = (mz * xi + Math.sqrt(k2)) / denom;
  return [k * mx, k * my, k * mz - xi];
}

export function computeEpipolarCurveDs(
  srcPx: [number, number],
  srcDs: DS,
  dstDs: DS,
  R: Mat3,
  T: Vec3,
  dstSize: [number, number],
  nPoints = 80
): [number, number][] {
  const ray = unprojectDs(srcPx[0], srcPx[1], srcDs);
  if (!ray) return [];
  const [W, H] = dstSize;
  const result: [number, number][] = [];

  for (let i = 0; i < nPoints; i++) {
    const depth = 0.2 * 60 ** (i / (nPoints - 1));
    const P_src: Vec3 = [ray[0] * depth, ray[1] * depth, ray[2] * depth];
    const rot = mat3MulVec3(R, P_src);
    const P_dst: Vec3 = [rot[0] + T[0], rot[1] + T[1], rot[2] + T[2]];

    const px = projectDs(P_dst, dstDs);
    if (!px) continue;
    const [pu, pv] = px;
    if (pu < 0 || pu >= W || pv < 0 || pv >= H) continue;
    result.push([pu, pv]);
  }

  return result;
}

// Find the nearest sampled point on the curve to (px, py).
export function snapToCurve(
  px: number,
  py: number,
  curve: [number, number][]
): [number, number] | null {
  if (curve.length === 0) return null;
  let bestD2 = Infinity;
  let best = curve[0];
  for (const pt of curve) {
    const d2 = (pt[0] - px) ** 2 + (pt[1] - py) ** 2;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = pt;
    }
  }
  return best;
}
